import React, { useState, useEffect } from 'react';
import { Plus, Building2, MapPin, Calendar, Edit2, Power, Mail, Phone, X, Send, Tag, BellRing } from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, query, where, writeBatch } from 'firebase/firestore';
import { sendWelcomeEmail, resendCredentials } from '../../services/emailService';
import { deleteDocument } from '../../services/firestoreService';
import { calculateLastSixMonthsRevenue, calculateSystemNetReceipt, getPlanForRevenue, buildBillingAlertMessage, DEFAULT_BILLING_PLANS } from '../../utils/billingPlans';
import LoadingState from '../shared/LoadingState';
import './Companies.css';

const Companies = () => {
    const businessTypes = [
      { value: 'tourism_agency', label: 'Agência de turismo' },
      { value: 'inn', label: 'Pousada' },
      { value: 'snack_bar', label: 'Lanchonete' },
      { value: 'boat', label: 'Lancha' },
      { value: 'schooner', label: 'Escuna' },
      { value: 'bar', label: 'Bar' },
      { value: 'restaurant', label: 'Restaurante' },
      { value: 'mixed', label: 'Operação mista' }
    ];

    const getBusinessTypeLabel = (value) => {
      return businessTypes.find(type => type.value === value)?.label || 'Não informado';
    };

    const formatCurrency = value => new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(value) || 0);

    const deleteCompany = async (company) => {
      if (!window.confirm(`Tem certeza que deseja excluir a empresa "${company.name}"? Esta ação não poderá ser desfeita.`)) {
        return;
      }
      try {
          const result = await deleteDocument('companies', company.id);
          if (result.error) {
            alert('Erro ao excluir empresa: ' + result.error);
          } else {
            alert('Empresa excluída com sucesso!');
            setCompanies(prev => prev.filter(c => c.id !== company.id));
          }
        } catch (error) {
        console.error('Erro ao excluir empresa:', error);
        alert('Erro ao excluir empresa: ' + error.message);
      }
    };
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    businessType: 'tourism_agency',
    plan: 'basic',
    billingPlan: 'plan-1',
    billingCycle: 'monthly',
    dueDay: 10,
    monthlyPrice: 1500,
    systemRevenuePercentage: 0,
    monthlyCostDeductionPercentage: 0,
    active: true
  });

  const PLAN_LABELS = {
    'plan-1': 'Plano 1',
    'plan-2': 'Plano 2',
    'plan-3': 'Plano 3',
    'plan-4': 'Plano 4',
    basic: 'Básico',
    pro: 'Pro',
    enterprise: 'Enterprise'
  };

  const getLegacyPlanFromBillingPlan = (billingPlan) => {
    if (billingPlan === 'plan-2') return 'pro';
    if (billingPlan === 'plan-3' || billingPlan === 'plan-4') return 'enterprise';
    return 'basic';
  };

  const getCompanyBillingSummary = (company) => {
    const settings = company?.billingSettings || {};
    const revenue = calculateLastSixMonthsRevenue(company?.reservations || [], new Date());
    const computedPlan = getPlanForRevenue(revenue, {
      recurringMonthly: settings.recurringMonthly !== false,
      customMonthlyPrice: settings.customMonthlyPrice ?? settings.monthlyPrice,
      dueDay: settings.dueDay || 10
    });

    const planId = company?.billingPlan || computedPlan.planId;
    const planMeta = DEFAULT_BILLING_PLANS[planId] || computedPlan.plan;
    const monthlyPrice = Number(settings?.customMonthlyPrice ?? settings?.monthlyPrice ?? computedPlan.monthlyPrice ?? planMeta.defaultMonthlyPrice);

    return {
      ...computedPlan,
      planId,
      planMeta,
      monthlyPrice,
      dueDay: Number(settings?.dueDay || computedPlan.dueDay || 10),
      recurringMonthly: settings?.recurringMonthly !== false,
      revenue,
      label: PLAN_LABELS[planId] || planMeta.label
    };
  };

  const syncCompanyBilling = async (company) => {
    const companyId = company.id;
    const reservationsSnapshot = await getDocs(collection(db, `companies/${companyId}/reservations`));
    const reservations = reservationsSnapshot.docs.map(item => ({ id: item.id, ...item.data() }));
    const revenue = calculateLastSixMonthsRevenue(reservations, new Date());
    const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const validReservations = reservations.filter(item => ['confirmed', 'completed'].includes(item.status) && Number(item.paidAmount || 0) > 0);
    const totalRevenue = validReservations.reduce((sum, item) => sum + Number(item.paidAmount || 0), 0);
    const monthRevenue = validReservations.reduce((sum, item) => {
      const createdAt = item.createdAt?.toDate ? item.createdAt.toDate() : new Date(item.createdAt || 0);
      return createdAt >= firstDayOfMonth ? sum + Number(item.paidAmount || 0) : sum;
    }, 0);
    const requestedPlan = company.billingPlan || getPlanForRevenue(revenue).planId;
    const selectedPlan = getPlanForRevenue(revenue, {
      dueDay: Number(company.billingSettings?.dueDay || 10),
      recurringMonthly: company.billingSettings?.recurringMonthly !== false,
      customMonthlyPrice: company.billingSettings?.customMonthlyPrice ?? company.billingSettings?.monthlyPrice
    });

    const billingSettings = {
      ...(company.billingSettings || {}),
      recurringMonthly: company.billingSettings?.recurringMonthly !== false,
      dueDay: Number(company.billingSettings?.dueDay || selectedPlan.dueDay || 10),
      monthlyPrice: Number(company.billingSettings?.customMonthlyPrice ?? company.billingSettings?.monthlyPrice ?? selectedPlan.monthlyPrice),
      customMonthlyPrice: company.billingSettings?.customMonthlyPrice ?? company.billingSettings?.monthlyPrice ?? null,
      cycle: company.billingSettings?.cycle || 'monthly',
      lastComputedRevenue: revenue,
      lastComputedAt: serverTimestamp()
    };

    const nextPlanId = requestedPlan || selectedPlan.planId;
    const legacyPlan = getLegacyPlanFromBillingPlan(nextPlanId);

    await updateDoc(doc(db, 'companies', companyId), {
      billingPlan: nextPlanId,
      billingSettings,
      plan: legacyPlan,
      updatedAt: serverTimestamp()
    });

    return {
      ...selectedPlan,
      planId: nextPlanId,
      billingSettings,
      revenue,
      totalRevenue,
      monthRevenue,
      ...calculateSystemNetReceipt({
        monthRevenue,
        monthlyPrice: billingSettings.monthlyPrice,
        systemRevenuePercentage: billingSettings.systemRevenuePercentage,
        monthlyCostDeductionPercentage: billingSettings.monthlyCostDeductionPercentage
      })
    };
  };

  const sendBillingDueAlert = async (company) => {
    const today = new Date();
    const summary = getCompanyBillingSummary(company);
    const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const dueDay = summary.dueDay;

    if (today.getDate() !== dueDay || company?.billingSettings?.lastAlertMonthKey === currentMonthKey) {
      return;
    }

    const userQuery = query(
      collection(db, 'users'),
      where('companyId', '==', company.id),
      where('role', 'in', ['admin'])
    );
    const userSnap = await getDocs(userQuery);
    const notifications = userSnap.docs.map(docItem => ({ id: docItem.id, ...docItem.data() }));

    if (notifications.length === 0) {
      return;
    }

    const message = buildBillingAlertMessage({
      plan: summary.planMeta,
      monthlyPrice: summary.monthlyPrice,
      dueDay: summary.dueDay
    });

    const batch = writeBatch(db);
    notifications.forEach(userItem => {
      const notifRef = doc(collection(db, `companies/${company.id}/userNotifications`));
      batch.set(notifRef, {
        companyId: company.id,
        recipientUserId: userItem.uid || userItem.id,
        recipientEmail: userItem.email || '',
        title: 'Cobrança do plano',
        message,
        read: false,
        createdAt: serverTimestamp(),
        type: 'billing_due',
        actionPath: '/',
        metadata: {
          companyId: company.id,
          billingPlan: summary.planId,
          dueDay: summary.dueDay,
          monthlyPrice: summary.monthlyPrice
        }
      });
    });

    batch.update(doc(db, 'companies', company.id), {
      'billingSettings.lastAlertMonthKey': currentMonthKey,
      'billingSettings.lastAlertAt': serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    await batch.commit();
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const companiesSnap = await getDocs(collection(db, 'companies'));
      const companiesList = companiesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const billingResults = new Map();
      for (const company of companiesList) {
        const result = await syncCompanyBilling(company).catch(() => null);
        if (result) billingResults.set(company.id, result);
        await sendBillingDueAlert(company).catch(() => {});
      }

      const refreshedCompanies = await getDocs(collection(db, 'companies'));
      const updatedList = refreshedCompanies.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        totalRevenue: billingResults.get(doc.id)?.totalRevenue || 0,
        monthRevenue: billingResults.get(doc.id)?.monthRevenue || 0,
        netReceipt: billingResults.get(doc.id)?.netReceipt || 0,
        lastSixMonthsRevenue: billingResults.get(doc.id)?.revenue || doc.data().billingSettings?.lastComputedRevenue || 0
      }));
      setCompanies(updatedList);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      alert('Erro ao carregar empresas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name,
        email: company.email,
        phone: company.phone || '',
        city: company.city,
        businessType: company.businessType || 'tourism_agency',
        plan: company.plan,
        billingPlan: company.billingPlan || 'plan-1',
        billingCycle: company.billingSettings?.cycle || 'monthly',
        dueDay: company.billingSettings?.dueDay || 10,
        monthlyPrice: company.billingSettings?.customMonthlyPrice ?? company.billingSettings?.monthlyPrice ?? 1500,
        active: company.active,
        systemRevenuePercentage: company.billingSettings?.systemRevenuePercentage ?? 0,
        monthlyCostDeductionPercentage: company.billingSettings?.monthlyCostDeductionPercentage ?? 0,
      });
    } else {
      setEditingCompany(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        businessType: 'tourism_agency',
        plan: 'basic',
        active: true,
        systemRevenuePercentage: 0,
        monthlyCostDeductionPercentage: 0,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCompany(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.city) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Email inválido');
      return;
    }

    try {
      if (editingCompany) {
        // Atualizar empresa existente
        const companyRef = doc(db, 'companies', editingCompany.id);
        await updateDoc(companyRef, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          businessType: formData.businessType,
          plan: getLegacyPlanFromBillingPlan(formData.billingPlan),
          billingPlan: formData.billingPlan,
          billingSettings: {
            ...(editingCompany.billingSettings || {}),
            cycle: formData.billingCycle,
            recurringMonthly: formData.billingCycle === 'monthly',
            dueDay: Number(formData.dueDay || 10),
            monthlyPrice: Number(formData.monthlyPrice || 0),
            customMonthlyPrice: Number(formData.monthlyPrice) || null,
            systemRevenuePercentage: Number(formData.systemRevenuePercentage || 0),
            monthlyCostDeductionPercentage: Number(formData.monthlyCostDeductionPercentage || 0)
          },
          active: formData.active,
          updatedAt: serverTimestamp()
        });
        alert('Empresa atualizada com sucesso!');
      } else {
        const computedPlan = getPlanForRevenue(0, {
          recurringMonthly: formData.billingCycle === 'monthly',
          customMonthlyPrice: Number(formData.monthlyPrice) || 1500,
          dueDay: Number(formData.dueDay || 10)
        });

        const companyPayload = {
          ...formData,
          plan: getLegacyPlanFromBillingPlan(formData.billingPlan || computedPlan.planId),
          billingPlan: formData.billingPlan || computedPlan.planId,
          billingSettings: {
            cycle: formData.billingCycle || 'monthly',
            recurringMonthly: formData.billingCycle === 'monthly',
            dueDay: Number(formData.dueDay || computedPlan.dueDay || 10),
            monthlyPrice: Number(formData.monthlyPrice || computedPlan.monthlyPrice || 1500),
            customMonthlyPrice: Number(formData.monthlyPrice) || null,
            systemRevenuePercentage: Number(formData.systemRevenuePercentage || 0),
            monthlyCostDeductionPercentage: Number(formData.monthlyCostDeductionPercentage || 0),
            lastComputedRevenue: 0,
            lastAlertMonthKey: null,
            lastAlertAt: null
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        const companyRef = await addDoc(collection(db, 'companies'), companyPayload);

        // Enviar email de boas-vindas
        const emailResult = await sendWelcomeEmail({
          companyId: companyRef.id,
          email: formData.email,
          name: formData.name
        });

        if (emailResult.success) {
          alert(`✅ Empresa cadastrada com sucesso!\n\nEmail enviado para: ${formData.email}\nSenha temporária: ${emailResult.data.temporaryPassword}\n\nO usuário receberá instruções de acesso.`);
        } else {
          alert(`⚠️ Empresa cadastrada, mas houve erro ao enviar email:\n${emailResult.error}\n\nConfigure o envio de email nas Functions.`);
        }
      }

      closeModal();
      loadCompanies();
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      alert('Erro ao salvar empresa: ' + error.message);
    }
  };

  const resendEmail = async (company) => {
    if (!window.confirm(`Deseja reenviar email de credenciais para ${company.email}?`)) {
      return;
    }

    try {
      const result = await resendCredentials(company.id);
      
      if (result.success) {
        alert(`✅ Email reenviado com sucesso para ${company.email}!\n\nNova senha temporária: ${result.data.temporaryPassword}`);
      } else {
        alert(`❌ Erro ao reenviar email:\n${result.error}`);
      }
    } catch (error) {
      console.error('Erro ao reenviar email:', error);
      alert('Erro ao reenviar email: ' + error.message);
    }
  };

  const toggleCompanyStatus = async (company) => {
    try {
      const companyRef = doc(db, 'companies', company.id);
      await updateDoc(companyRef, {
        active: !company.active,
        updatedAt: serverTimestamp()
      });
      loadCompanies();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Erro ao alterar status: ' + error.message);
    }
  };

  if (loading) {
    return <LoadingState title="Carregando empresas" subtitle="Conferindo contas e planos ativos" />;
  }

  return (
    <div className="companies-container">
      <div className="companies-header">
        <h1>Empresas</h1>
        <button className="btn-new-company" onClick={() => openModal()}>
          <Plus size={20} />
          Nova Empresa
        </button>
      </div>

      {companies.length === 0 ? (
        <div className="empty-state">
          <Building2 size={64} />
          <h3>Nenhuma empresa cadastrada</h3>
          <p>Clique em "Nova Empresa" para cadastrar a primeira empresa</p>
        </div>
      ) : (
        <>
        <div className="company-billing-table-wrap">
          <table className="company-billing-table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Plano</th>
                <th>Mensalidade</th>
                <th>Vencimento</th>
                <th>Situação</th>
                <th>Faturamento do mês</th>
                <th>Últimos 6 meses</th>
                <th>Recebimento líquido</th>
              </tr>
            </thead>
            <tbody>
              {companies.map(company => {
                const billing = getCompanyBillingSummary(company);
                const now = new Date();
                const dueDay = Math.min(billing.dueDay, new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate());
                const dueDate = new Date(now.getFullYear(), now.getMonth(), dueDay, 12);
                const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const isFree = company.billingSettings?.freeMonthKey === monthKey;
                const isPaid = company.billingSettings?.lastPaidMonthKey === monthKey;
                const isOverdue = !isFree && !isPaid && dueDate < now;
                return (
                  <tr key={`billing-company-${company.id}`}>
                    <td data-label="Empresa"><strong>{company.name}</strong><small>{company.city || '-'}</small></td>
                    <td data-label="Plano">{billing.label}</td>
                    <td data-label="Mensalidade">{formatCurrency(billing.monthlyPrice)}</td>
                    <td data-label="Vencimento">{dueDate.toLocaleDateString('pt-BR')}</td>
                    <td data-label="Situação"><span className={`company-billing-status ${isFree ? 'free' : isPaid ? 'paid' : isOverdue ? 'overdue' : 'scheduled'}`}>{isFree ? 'Gratuita' : isPaid ? 'Paga' : isOverdue ? 'Vencida' : 'Programada'}</span></td>
                    <td data-label="Faturamento do mês">{formatCurrency(company.monthRevenue)}</td>
                    <td data-label="Últimos 6 meses">{formatCurrency(company.lastSixMonthsRevenue)}</td>
                    <td data-label="Recebimento líquido"><strong className="company-net-receipt">{formatCurrency(company.netReceipt)}</strong></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="companies-grid">
          {companies.map(company => (
            <div key={company.id} className="company-card">
              <div className="company-card-header">
                <div>
                  <h3 className="company-name">{company.name}</h3>
                </div>
                <span className={`company-status ${company.active ? 'active' : 'inactive'}`}>
                  {company.active ? 'Ativa' : 'Inativa'}
                </span>
              </div>

              <div className="company-info">
                <div className="company-info-item">
                  <Mail size={16} />
                  {company.email}
                </div>
                {company.phone && (
                  <div className="company-info-item">
                    <Phone size={16} />
                    {company.phone}
                  </div>
                )}
                <div className="company-info-item">
                  <MapPin size={16} />
                  {company.city}
                </div>
                <div className="company-info-item">
                  <Tag size={16} />
                  Tipo: {getBusinessTypeLabel(company.businessType)}
                </div>
                <div className="company-info-item">
                  <Calendar size={16} />
                  Plano: {PLAN_LABELS[company.billingPlan] || (company.plan === 'basic' ? 'Básico' : company.plan === 'pro' ? 'Pro' : 'Enterprise')}
                </div>
              </div>

              <div className="company-actions">
                <button className="btn-action btn-edit" onClick={() => openModal(company)}>
                  <Edit2 size={16} />
                  Editar
                </button>
                <button className="btn-action btn-resend" onClick={() => resendEmail(company)} title="Reenviar credenciais">
                  <Send size={16} />
                  Reenviar Email
                </button>
                <button className="btn-action btn-toggle" onClick={() => toggleCompanyStatus(company)}>
                  <Power size={16} />
                  {company.active ? 'Desativar' : 'Ativar'}
                </button>
                <button className="btn-action btn-delete" aria-label={`Excluir ${company.name}`} onClick={() => deleteCompany(company)} title="Excluir empresa">
                  <X size={16} />
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCompany ? 'Editar Empresa' : 'Nova Empresa'}</h2>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome da Empresa *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Turismo Paraíso"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contato@empresa.com"
                      required
                      disabled={editingCompany}
                    />
                    {!editingCompany && (
                      <small style={{ color: '#64748b', fontSize: '0.85rem' }}>
                        Um email será enviado com as credenciais de acesso
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Telefone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Percentual do sistema sobre o faturamento (%)</label>
                    <input type="number" min="0" max="100" step="0.01" value={formData.systemRevenuePercentage} onChange={e => setFormData({ ...formData, systemRevenuePercentage: Number(e.target.value || 0) })} />
                    <small>Aplicado sobre o faturamento mensal pago da empresa.</small>
                  </div>
                  <div className="form-group">
                    <label>Desconto do custo mensal (%)</label>
                    <input type="number" min="0" max="100" step="0.01" value={formData.monthlyCostDeductionPercentage} onChange={e => setFormData({ ...formData, monthlyCostDeductionPercentage: Number(e.target.value || 0) })} />
                    <small>Percentual descontado do valor da mensalidade.</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Cidade *</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={e => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Ex: Florianópolis"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Tipo de Negócio</label>
                    <select
                      value={formData.businessType}
                      onChange={e => setFormData({ ...formData, businessType: e.target.value })}
                    >
                      {businessTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Plano de faturamento</label>
                    <select
                      value={formData.billingPlan}
                      onChange={e => {
                        const nextBillingPlan = e.target.value;
                        const billingPrice = DEFAULT_BILLING_PLANS[nextBillingPlan]?.defaultMonthlyPrice || 1500;
                        setFormData({
                          ...formData,
                          billingPlan: nextBillingPlan,
                          plan: getLegacyPlanFromBillingPlan(nextBillingPlan),
                          monthlyPrice: billingPrice,
                          dueDay: Number(formData.dueDay || 10)
                        });
                      }}
                    >
                      <option value="plan-1">Plano 1 — até R$ 500 mil</option>
                      <option value="plan-2">Plano 2 — R$ 500.001 até R$ 1M</option>
                      <option value="plan-3">Plano 3 — R$ 1.000.001 até R$ 1,5M</option>
                      <option value="plan-4">Plano 4 — acima de R$ 1,5M</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Periodicidade</label>
                    <select
                      value={formData.billingCycle}
                      onChange={e => setFormData({ ...formData, billingCycle: e.target.value })}
                    >
                      <option value="monthly">Mensal</option>
                      <option value="annual">Anual</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Dia de cobrança</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.dueDay}
                      onChange={e => setFormData({ ...formData, dueDay: Number(e.target.value || 10) })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Valor da mensalidade</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.monthlyPrice}
                      onChange={e => setFormData({ ...formData, monthlyPrice: Number(e.target.value || 0) })}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit">
                  {editingCompany ? 'Salvar Alterações' : 'Cadastrar Empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
